import {createAsyncThunk} from '@reduxjs/toolkit'

import {minZero} from '@shared/helpers/lang'
import {qs} from '@shared/helpers/queryString'

import {OrderSheetPageSet, OrderSheetPageSetType, ChargePointPolicyType} from '@web/core/constants'
import getApi, {createWrapper, getBaseUrl, asyncActionWrapper, createApiAsyncThunk} from '@web/helpers/api'
import {ChargePointState, chargePointSelector, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {CommonState, entryToServiceSequence, pageSetInfoSelector} from '@web/slices/common/slice'
import {RewardPointState, rewardPointSelector} from '@web/slices/rewardPoint/slice'

import {benefitApi} from './apis'
import {
    RewardPointBalanceReloadResponse,
    ChargePointBalanceReloadResponse,
    RechargeableChargePointReloadResponse,
    NaverpayPointBalanceReloadResponse,
    PointError,
    RewardPointWithVoucherReloadResponse,
    PayChannel,
    RewardPointBalance,
    RewardPoint,
} from './interfaces'

type RootState = {
    common: CommonState
    rewardPoint: RewardPointState
    chargePoint: ChargePointState
}

type NaverPointPayload = {
    /**
     * rewardPoint > 적립포인트 총 잔액
     * cashRewardPoint > 적립포인트 내 현금성 포인트
     */
    /** 적립포인트 잔액 (현금성 X) */
    rewardPoint: number
    /** 현금성 적립포인트 잔액 */
    cashRewardPoint: number
    chargePointAmount: number
    isPayMoneyUser: boolean
    /** 충전포인트 사용정책 */
    chargePointPolicy: ChargePointPolicyType
}

const PointApiUrlInfo = {
    [OrderSheetPageSet.MALL]: {
        /** 충전포인트와 적립포인트 잔액 재조회 */
        naverpayPointBalance: '/orderSheet/mall/naverpayPointBalance/v2',
        /** 적립 포인트 잔액 재조회 */
        rewardPointBalance: '/orderSheet/mall/rewardPointBalance/v2',
        /** 충전포인트 잔액 재조회 */
        chargePointBalance: '/orderSheet/mall/chargePointBalance',
        /** 충전포인트 충전 가능 금액 재조회 */
        rechargeableChargePoint: '/orderSheet/mall/rechargeableChargePoint',
    },
    [OrderSheetPageSet.EASYBOOKING]: {
        /** 충전포인트와 적립포인트 잔액 재조회 */
        naverpayPointBalance: '/orderSheet/easyBooking/naverpayPointBalance/v2',
        /** 적립 포인트 잔액 재조회 */
        rewardPointBalance: '/orderSheet/easyBooking/rewardPointBalance/v2',
        /** 충전포인트 잔액 재조회 */
        chargePointBalance: '/orderSheet/easyBooking/chargePointBalance',
        /** 충전포인트 충전 가능 금액 재조회 */
        rechargeableChargePoint: '/orderSheet/easyBooking/rechargeableChargePoint',
    },
    [OrderSheetPageSet.SELLER]: {
        /** 충전포인트와 적립포인트 잔액 재조회 */
        naverpayPointBalance: '/orderSheet/seller/naverpayPointBalance/v2',
        /** 적립 포인트 잔액 재조회 */
        rewardPointBalance: '/orderSheet/seller/rewardPointBalance/v2',
        /** 충전포인트 잔액 재조회 */
        chargePointBalance: '/orderSheet/seller/chargePointBalance',
        /** 충전포인트 충전 가능 금액 재조회 */
        rechargeableChargePoint: '/orderSheet/seller/rechargeableChargePoint',
    },
    [OrderSheetPageSet.FAMILY_PAY]: {
        /** 충전포인트와 적립포인트 잔액 재조회 */
        naverpayPointBalance: '/orderSheet/familyPay/naverpayPointBalance/v2',
        /** 적립 포인트 잔액 재조회 */
        rewardPointBalance: '/orderSheet/familyPay/rewardPointBalance/v2',
        /** 충전포인트 잔액 재조회 */
        chargePointBalance: '/orderSheet/familyPay/chargePointBalance',
        /** 충전포인트 충전 가능 금액 재조회 */
        rechargeableChargePoint: '/orderSheet/familyPay/rechargeableChargePoint',
    },
    [OrderSheetPageSet.CLAIM]: {
        // todo - feat/19429: 클레임 적립포인트 api 준비되면 수정 필요
        /** 충전포인트와 적립포인트 잔액 재조회 */
        naverpayPointBalance: '',
        /** 적립 포인트 잔액 재조회 */
        rewardPointBalance: '',
        /** 충전포인트 잔액 재조회 */
        chargePointBalance: '',
        /** 충전포인트 충전 가능 금액 재조회 */
        rechargeableChargePoint: '',
    },
}

const api = (pageSet: OrderSheetPageSetType) => getApi(getBaseUrl(pageSet))

function getNaverPointPayload(state: RootState): NaverPointPayload {
    return {
        rewardPoint: rewardPointSelector(state).amount,
        cashRewardPoint: rewardPointSelector(state).cashPoint,
        chargePointAmount: chargePointSelector(state).amount,
        isPayMoneyUser: payMoneyInfoSelector(state).isPayMoneyUser,
        chargePointPolicy: chargePointSelector(state).policy,
    }
}

/** 충전포인트 & 적립포인트 잔액 재조회 */
export const getAllPointBalance = createApiAsyncThunk<
    {
        data: NaverpayPointBalanceReloadResponse
    } & NaverPointPayload,
    {pageSet: OrderSheetPageSetType},
    {
        state: RootState
        rejectValue: PointError
    }
>('payPoint/getAllPointBalance', async ({pageSet}, {getState}) => {
    const state = getState()

    const result = await createWrapper<NaverpayPointBalanceReloadResponse>(
        api(pageSet).get(
            qs.stringifyUrl({
                url: PointApiUrlInfo[pageSet].naverpayPointBalance,
                query: {
                    orderSheetId: state.common.seqID,
                },
            }),
        ),
    )

    const chargePointAmount = result.chargePointBalance.availableBalance ?? 0
    const rewardPoint = result.rewardPoint.rewardPointBalance ?? 0
    const cashRewardPoint = result.rewardPoint.cashableRewardPointBalance ?? 0

    return {
        ...getNaverPointPayload(getState()),
        data: result,
        chargePointAmount: result.chargePointBalance.resultType === 'SUCCESS' ? chargePointAmount : 0,
        rewardPoint: result.rewardPoint.success ? rewardPoint : 0,
        cashRewardPoint: result.rewardPoint.success ? cashRewardPoint : 0,
    }
})

/** 적립포인트 재조회  */
export const getRewardPointBalance = createApiAsyncThunk<
    {
        data: RewardPointBalanceReloadResponse
    } & NaverPointPayload,
    {pageSet: OrderSheetPageSetType},
    {
        state: RootState
        rejectValue: PointError
    }
>('payPoint/getRewardPointBalance', async ({pageSet}, {getState}) => {
    const state = getState()

    const result = await createWrapper<RewardPointBalanceReloadResponse>(
        api(pageSet).get(
            qs.stringifyUrl({
                url: PointApiUrlInfo[pageSet].rewardPointBalance,
                query: {
                    orderSheetId: state.common.seqID,
                },
            }),
        ),
    )

    const rewardPoint = result.rewardPointBalance ?? 0
    const cashRewardPoint = result.cashableRewardPointBalance ?? 0
    return {
        ...getNaverPointPayload(getState()),
        data: result,
        rewardPoint: result.success ? rewardPoint : 0,
        cashRewardPoint: result.success ? cashRewardPoint : 0,
    }
})

/**
 * 적립포인트 재조회 (혜택 BE 조회)
 * 디컨단건, 디컨정기에서 사용중 (25.04.16)
 * 주문형 정기에서 사용 (25.07.23)
 */
export const getRewardPointBalanceWithVoucher = createAsyncThunk<
    {data: RewardPointWithVoucherReloadResponse; totalBalance: number},
    void,
    {
        state: RootState
        rejectValue: PointError
    }
>('payPoint/getRewardPointBalanceWithVoucher', async (_, {getState, rejectWithValue}) => {
    return asyncActionWrapper(
        async () => {
            const state = getState()
            const {isPaymentMethodManagement, isDiconGroup} = pageSetInfoSelector(state)
            const {isPayMoneyUser} = payMoneyInfoSelector(state)
            const payChannel = (() => {
                if (isDiconGroup) {
                    return PayChannel.DICON
                }
                return null
            })()

            const params = {
                type: isPaymentMethodManagement
                    ? RewardPointBalance.INCLUDE_TOTAL_VOUCHER
                    : RewardPointBalance.INCLUDE_VOUCHER,
                include: [RewardPoint.WELFARE],
                ...(payChannel ? {payChannel} : {}),
                ...(payChannel === 'DICON' ? {serviceSeq: entryToServiceSequence(state.common.entry)} : {}),
            }

            const result = await benefitApi.fetchRewardPointWithVoucher(params)

            /**
             * 총 네이버 포인트
             * - 페이머니 유저인 경우: 적립 포인트(음수인 경우 0원처리)
             * - 페이머니 유저가 아닌 경우: 적립 포인트(음수인 경우 0원처리) + 충전 포인트(음수인 경우 0원처리)
             */
            const totalBalance = isPayMoneyUser
                ? minZero(result.balanceAmount)
                : minZero(result.balanceAmount) + minZero(state.chargePoint.actualAmount)

            return {
                data: result,
                totalBalance,
            }
        },
        {
            rejectWithValue,
        },
    )
})

/** 충전포인트 재조회 */
export const getChargePointBalance = createApiAsyncThunk<
    {data: ChargePointBalanceReloadResponse} & NaverPointPayload,
    {pageSet: OrderSheetPageSetType},
    {
        state: RootState
        rejectValue: PointError
    }
>('payPoint/getChargePointBalance', async ({pageSet}, {getState}) => {
    const state = getState()
    const orderSheetId = state.common.seqID

    const result = await createWrapper<ChargePointBalanceReloadResponse>(
        api(pageSet).get(PointApiUrlInfo[pageSet].chargePointBalance, {
            params: {
                ...(pageSet === OrderSheetPageSet.FAMILY_PAY && orderSheetId ? {orderNo: orderSheetId} : {}),
            },
        }),
    )

    const chargePointAmount = result.availableBalance ?? 0

    return {
        ...getNaverPointPayload(state),
        data: result,
        chargePointAmount: result.resultType === 'SUCCESS' ? chargePointAmount : 0,
    }
})

/** 충전포인트 충전한도 재조회 */
export const getChargeableAmount = createApiAsyncThunk<
    RechargeableChargePointReloadResponse,
    {pageSet: OrderSheetPageSetType},
    {
        state: RootState
        rejectValue: PointError
    }
>('payPoint/getRechargeableAmount', async ({pageSet}) => {
    const result = await createWrapper<RechargeableChargePointReloadResponse>(
        api(pageSet).patch(PointApiUrlInfo[pageSet].rechargeableChargePoint),
    )

    return result
})
